import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, Trash2, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Customer, Projection, ProjectionStatus } from '../types';
import moment from 'moment';

interface ProjectionModalProps {
  projection?: Projection;
  customers: Customer[];
  onClose: () => void;
  onSave: () => void;
  isNew?: boolean;
}

function ProjectionModal({ projection, customers, onClose, onSave, isNew = false }: ProjectionModalProps) {
  const [editedProjection, setEditedProjection] = useState<Partial<Projection>>(
    projection || {
      customer: customers[0],
      project_name: '',
      year: new Date().getFullYear().toString(),
      value: '',
      confidence: 50,
      status: 'alive' as ProjectionStatus,
      notes: []
    }
  );
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const years = Array.from(
    { length: 5 },
    (_, i) => (new Date().getFullYear() + i).toString()
  );

  const formatValue = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(parseInt(numericValue, 10));
    }
    return '';
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!editedProjection.customer?.id || !editedProjection.project_name?.trim() || !editedProjection.value) {
        throw new Error('Please fill in all required fields');
      }

      if (isNew) {
        const { data: newProjection, error: createError } = await supabase
          .from('projections')
          .insert({
            customer_id: editedProjection.customer.id,
            project_name: editedProjection.project_name,
            year: editedProjection.year,
            value: editedProjection.value,
            confidence: editedProjection.confidence,
            status: editedProjection.status
          })
          .select()
          .single();

        if (createError) throw createError;

        if (newNote.trim()) {
          const { error: noteError } = await supabase
            .from('projection_notes')
            .insert({
              projection_id: newProjection.id,
              description: newNote.trim()
            });

          if (noteError) throw noteError;
        }
      } else if (projection?.id) {
        const { error: updateError } = await supabase
          .from('projections')
          .update({
            customer_id: editedProjection.customer?.id,
            project_name: editedProjection.project_name,
            year: editedProjection.year,
            value: editedProjection.value,
            confidence: editedProjection.confidence,
            status: editedProjection.status
          })
          .eq('id', projection.id);

        if (updateError) throw updateError;

        if (newNote.trim()) {
          const { error: noteError } = await supabase
            .from('projection_notes')
            .insert({
              projection_id: projection.id,
              description: newNote.trim()
            });

          if (noteError) throw noteError;
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving projection:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isNew ? 'New Projection' : 'Edit Projection'}
            </h2>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer
              </label>
              <select
                value={editedProjection.customer?.id}
                onChange={(e) => {
                  const customer = customers.find(c => c.id === e.target.value);
                  setEditedProjection({ ...editedProjection, customer });
                }}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name
              </label>
              <input
                type="text"
                value={editedProjection.project_name}
                onChange={(e) => setEditedProjection({
                  ...editedProjection,
                  project_name: e.target.value
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter project name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                value={editedProjection.year}
                onChange={(e) => setEditedProjection({
                  ...editedProjection,
                  year: e.target.value
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="text"
                value={editedProjection.value}
                onChange={(e) => setEditedProjection({
                  ...editedProjection,
                  value: formatValue(e.target.value)
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter projected value"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confidence ({editedProjection.confidence}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={editedProjection.confidence}
                onChange={(e) => setEditedProjection({
                  ...editedProjection,
                  confidence: parseInt(e.target.value)
                })}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="alive"
                    checked={editedProjection.status === 'alive'}
                    onChange={(e) => setEditedProjection({
                      ...editedProjection,
                      status: e.target.value as ProjectionStatus
                    })}
                    className="form-radio text-indigo-600"
                  />
                  <span className="ml-2">Alive</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="dead"
                    checked={editedProjection.status === 'dead'}
                    onChange={(e) => setEditedProjection({
                      ...editedProjection,
                      status: e.target.value as ProjectionStatus
                    })}
                    className="form-radio text-red-600"
                  />
                  <span className="ml-2">Dead</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <div className="space-y-2">
                {editedProjection.notes?.map((note, index) => (
                  <div key={note.id} className="flex items-start space-x-2 p-2 bg-gray-50 rounded-md">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{note.description}</p>
                      <p className="text-xs text-gray-400">
                        {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Projection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sales() {
  const [projections, setProjections] = useState<Projection[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjection, setShowNewProjection] = useState(false);
  const [selectedProjection, setSelectedProjection] = useState<Projection | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectionsResponse, customersResponse] = await Promise.all([
        supabase
          .from('projections')
          .select(`
            *,
            customer:customers (
              id,
              name
            ),
            notes:projection_notes (
              id,
              description,
              created_at
            )
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('customers')
          .select('*')
          .order('name')
      ]);

      if (projectionsResponse.error) throw projectionsResponse.error;
      if (customersResponse.error) throw customersResponse.error;

      setProjections(projectionsResponse.data);
      setCustomers(customersResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 75) return 'bg-green-100 text-green-800';
    if (confidence >= 50) return 'bg-yellow-100 text-yellow-800';
    if (confidence >= 25) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading sales data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Sales Pipeline</h1>
          </div>
          <button
            onClick={() => setShowNewProjection(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Projection
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Year
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projections.map((projection) => (
                <tr
                  key={projection.id}
                  onClick={() => setSelectedProjection(projection)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {projection.customer.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {projection.project_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {projection.year}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {projection.value}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(projection.confidence)}`}>
                      {projection.confidence}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      projection.status === 'alive'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {projection.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {projection.notes.length > 0 && (
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {projection.notes.length}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(showNewProjection || selectedProjection) && (
          <ProjectionModal
            projection={selectedProjection || undefined}
            customers={customers}
            onClose={() => {
              setShowNewProjection(false);
              setSelectedProjection(null);
            }}
            onSave={() => {
              loadData();
              setShowNewProjection(false);
              setSelectedProjection(null);
            }}
            isNew={!selectedProjection}
          />
        )}
      </div>
    </div>
  );
}